import { useCallback, useId, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TeammateTopic, TrendTopic } from '@mg/shared';
import { Plus, Sparkles, Tags, X } from 'lucide-react';
import { api, ApiRequestError } from '../../lib/api';
import { cn } from '../../lib/cn';
import { hcFillSelected } from '../../lib/forcedColors';
import { useTenantKey } from '../../lib/useTenantKey';
import { useAuth } from '../../auth/AuthContext';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Field';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/States';
import { TeammateTopicChip } from './TeammateTopicChip';
import { afterTopicAdopted, afterTopicRemoved } from './topicCache';
import {
  pendingTeammateTopics,
  suggestionView,
  topicKey,
  type SuggestionView,
} from './teammateTopics';

/** Split a raw input into discrete phrases on commas (and newlines), trimmed + de-empty. */
function splitPhrases(raw: string): string[] {
  return raw
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Move keyboard focus from the control that started an action (`fromId`) to one that is about to
 * exist (`id`). Adding a teammate's topic removes the button that was pressed, and undoing it removes
 * the × that was pressed; without this, focus falls back to the page body. The target is rendered a
 * frame or two later (the list re-renders from the query cache), so look for it briefly and then
 * give up — it is not there in every view.
 *
 * Only while focus is still on that control, or was dropped when it went away. An add is a network
 * round trip: by the time it answers the user may be typing in the text box, and pulling focus out
 * from under them would send their next keystroke to a button.
 */
function focusWhenPresent(id: string, fromId: string): void {
  let tries = 0;
  const tick = () => {
    const active = document.activeElement;
    if (active && active !== document.body && active.id !== fromId) return;
    const el = document.getElementById(id);
    if (el) {
      el.focus();
      return;
    }
    if (++tries < 30) requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

/**
 * Manage the signed-in user's Trend Topics — keyword/phrase tags that groom the Social Digest.
 * They steer how source items are clustered into themes and are what each theme's "% match" on
 * the Social Trends page is scored against (together with the user's Brand/Company/Personal Voice).
 *
 * The input accepts comma-separated values (each comma becomes its own tag). Below it, "Trend
 * Suggestions" offers topics to add in one click from two places: "Used by teammates" — what other
 * members of the workspace track that the user does not, added as the user's own copy — and AI
 * suggestions grounded in the workspace's Brand/Company Voice and the user's Personal Voice.
 */
export function TrendTopicsPanel() {
  const tenantKey = useTenantKey();
  const queryClient = useQueryClient();
  const { principal, updateUser } = useAuth();
  const inputId = useId();
  const shareId = useId();
  const tabsId = useId();
  const [draft, setDraft] = useState('');
  const [chosenView, setChosenView] = useState<SuggestionView | null>(null);
  // The chip whose USED BY popover is open (by topic key); one at a time.
  const [openUsedBy, setOpenUsedBy] = useState<string | null>(null);
  // Topics just added from a teammate's chip, by id, with the chip they came from. They keep a teal
  // outline while the user is on this tab, and their × takes them straight back out — the undo,
  // which puts that chip back.
  const [justAdded, setJustAdded] = useState<ReadonlyMap<string, TeammateTopic>>(() => new Map());
  // Said aloud by screen readers: a chip that vanishes into the list, or a topic that goes back to
  // being a suggestion, changes the page without anything else announcing it. `n` makes the same
  // sentence twice in a row a new announcement rather than no change at all.
  const [announcement, setAnnouncement] = useState({ text: '', n: 0 });
  const announce = (text: string) => setAnnouncement((prev) => ({ text, n: prev.n + 1 }));

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['trend-topics', tenantKey],
    queryFn: ({ signal }) => api.listTrendTopics(signal),
    enabled: tenantKey !== '',
  });

  const topics = data ?? [];

  // AI suggestions are an LLM call — cache generously and don't retry/refetch aggressively.
  const suggestionsQuery = useQuery({
    queryKey: ['trend-suggestions', tenantKey],
    queryFn: ({ signal }) => api.getTrendSuggestions(signal),
    enabled: tenantKey !== '',
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const teammatesQuery = useQuery({
    queryKey: ['teammate-topics', tenantKey],
    queryFn: ({ signal }) => api.getTeammateTopics(signal),
    enabled: tenantKey !== '',
    refetchOnWindowFocus: false,
  });

  const invalidateTopics = () => {
    void queryClient.invalidateQueries({ queryKey: ['trend-topics', tenantKey] });
    // Adding/removing a topic regenerates the digest — refresh Social Trends too.
    void queryClient.invalidateQueries({ queryKey: ['brief', tenantKey] });
  };

  const removeButtonId = (topicId: string) => `${tabsId}-remove-${topicId}`;
  // A topic key can hold spaces, which an element id cannot.
  const chipAddId = (key: string) => `${tabsId}-add-${encodeURIComponent(key)}`;

  // Single add — used by the click-to-add suggestion chips (keyed on the phrase string).
  const create = useMutation({
    mutationFn: (phrase: string) => api.createTrendTopic({ phrase }),
    onSuccess: invalidateTopics,
  });

  // Add a teammate's topic as the user's own copy, outlined in teal (see afterTopicAdopted for
  // what happens to the caches, and why the teammates list is left alone).
  const adopt = useMutation({
    mutationFn: (from: TeammateTopic) => api.createTrendTopic({ phrase: from.label }),
    onSuccess: (topic, from) => {
      afterTopicAdopted(queryClient, tenantKey, topic);
      setJustAdded((prev) => new Map(prev).set(topic.id, from));
      setOpenUsedBy(null);
      announce(`Added ${topic.phrase} to your topics.`);
      // The chip that was pressed is gone; its topic's × — the undo — is the natural next stop.
      focusWhenPresent(removeButtonId(topic.id), chipAddId(topicKey(from.label)));
    },
  });

  // Bulk add — used by the input, which may hold several comma-separated phrases.
  const bulk = useMutation({
    mutationFn: (phrases: string[]) => api.createTrendTopics(phrases),
    onSuccess: () => {
      setDraft('');
      invalidateTopics();
    },
  });

  const remove = useMutation({
    mutationFn: (topic: TrendTopic) => api.deleteTrendTopic(topic.id),
    onSuccess: (_void, topic) => {
      const cameFrom = justAdded.get(topic.id);
      afterTopicRemoved(queryClient, tenantKey, topic, cameFrom);
      announce(`Removed ${topic.phrase} from your topics.`);
      // Undo: the × that was pressed is gone, and the chip it came from is back.
      if (cameFrom) {
        focusWhenPresent(chipAddId(topicKey(cameFrom.label)), removeButtonId(topic.id));
      }
    },
  });

  // "Share my topics with teammates". Shown as switched the moment it is clicked, and the saved
  // user is folded back into the principal so the setting reads right on the next visit too.
  const [shareOverride, setShareOverride] = useState<boolean | null>(null);
  const sharing = shareOverride ?? principal?.user.shareTrendTopics ?? true;
  const share = useMutation({
    mutationFn: (next: boolean) => api.updateMeSettings({ shareTrendTopics: next }),
    onMutate: (next) => setShareOverride(next),
    onSuccess: (user) => updateUser(user),
    onSettled: () => setShareOverride(null),
  });

  const closeUsedBy = useCallback(() => setOpenUsedBy(null), []);

  function addTopics() {
    const phrases = splitPhrases(draft);
    if (phrases.length === 0) return;
    bulk.mutate(phrases);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTopics();
    }
  }

  // Hide suggestions the user already has (so an added one disappears on invalidate).
  const topicKeys = new Set(topics.map((t) => topicKey(t.phrase)));
  const suggestions = (suggestionsQuery.data ?? []).filter((s) => !topicKeys.has(topicKey(s)));
  const teamSuggestions = pendingTeammateTopics(teammatesQuery.data ?? [], topics);
  // Until the teammates list has answered, "none yet" is not "none": showing the AI view (and its
  // Refresh) for that moment and then swapping it for the teammates view is a visible jump.
  const teamLoading = teammatesQuery.isLoading;
  const view = suggestionView(chosenView, teamSuggestions.length);
  const showTabs = !teamLoading && teamSuggestions.length > 0;
  const tabId = (v: SuggestionView) => `${tabsId}-${v}-tab`;
  const panelId = `${tabsId}-panel`;

  // The two-tab control behaves like a tab list: arrows (and Home/End) move between the tabs, and
  // only the selected one is in the Tab order.
  function onTabKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    let next: SuggestionView | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') next = view === 'team' ? 'ai' : 'team';
    else if (e.key === 'Home') next = 'team';
    else if (e.key === 'End') next = 'ai';
    if (!next) return;
    e.preventDefault();
    setChosenView(next);
    focusWhenPresent(tabId(next), tabId(view));
  }

  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Tags className="h-4 w-4 text-action" aria-hidden="true" />
            Trend Topics
          </span>
        }
        description="Keyword/phrase tags that groom your Social Digest — they steer how content is grouped and are what the “% match” on the Social Trends page is scored against, together with your Brand/Company/Personal Voice."
      />
      <CardBody className="space-y-4">
        {isLoading ? (
          <LoadingState label="Loading trend topics…" />
        ) : isError ? (
          <ErrorState
            message={
              error instanceof ApiRequestError ? error.message : 'Could not load trend topics.'
            }
            onRetry={() => void refetch()}
          />
        ) : (
          <>
            <p className="sr-only" role="status" aria-live="polite">
              <span key={announcement.n}>{announcement.text}</span>
            </p>

            {topics.length > 0 ? (
              <ul className="flex flex-wrap gap-2">
                {topics.map((topic: TrendTopic) => {
                  const added = justAdded.has(topic.id);
                  return (
                    <li key={topic.id}>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full bg-tint px-2.5 py-1 text-xs font-medium text-tint-fg',
                          // The success colour, with its dark twin: the outline is the only thing
                          // marking the topic, so it has to stand out from the chip (3:1) in both
                          // themes (4.3:1 light, 6.9:1 dark; without the twin, 2.6:1 in dark).
                          added && 'ring-2 ring-success dark:ring-success-dark',
                        )}
                      >
                        {topic.phrase}
                        {added && <span className="sr-only"> (just added)</span>}
                        <button
                          id={removeButtonId(topic.id)}
                          type="button"
                          disabled={remove.isPending && remove.variables?.id === topic.id}
                          onClick={() => remove.mutate(topic)}
                          aria-label={`Remove ${topic.phrase}`}
                          className="focus-ring rounded-full text-ink-faint hover:text-tint-fg disabled:opacity-50"
                        >
                          <X className="h-3 w-3" aria-hidden="true" />
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <EmptyState
                title="No trend topics yet"
                description="Add your first keyword or phrase to sharpen how the digest groups content and scores each theme's % match."
              />
            )}

            <div className="flex gap-2">
              <label htmlFor={inputId} className="sr-only">
                Add trend topics
              </label>
              <Input
                id={inputId}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Add keywords, comma-separated — e.g. patient education, robotics"
                className="flex-1"
              />
              <Button type="button" variant="secondary" loading={bulk.isPending} onClick={addTopics}>
                Add
              </Button>
            </div>

            {bulk.isError && (
              <p className="text-xs text-red-600 dark:text-error-dark">
                {bulk.error instanceof ApiRequestError
                  ? bulk.error.message
                  : 'Could not add the topics.'}
              </p>
            )}

            <div>
              <label htmlFor={shareId} className="flex items-center gap-2.5 text-sm text-ink-strong">
                <input
                  id={shareId}
                  type="checkbox"
                  checked={sharing}
                  disabled={!principal}
                  // Not disabled while it saves — that would drop keyboard focus off the box. A
                  // second click during the save is simply not sent.
                  aria-busy={share.isPending || undefined}
                  onChange={(e) => {
                    if (!share.isPending) share.mutate(e.target.checked);
                  }}
                  className="focus-ring h-4 w-4 rounded border-line-input text-action"
                />
                Share my topics with teammates
              </label>
              {share.isError && (
                <p role="alert" className="mt-1 text-xs text-red-600 dark:text-error-dark">
                  Could not save that setting, so it is unchanged.
                </p>
              )}
            </div>

            {/* Click-to-add suggestions: teammates' topics, or AI ones grounded in the voices. */}
            <div className="border-t border-line-soft pt-4">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">
                  <Sparkles className="h-3.5 w-3.5 text-action" aria-hidden="true" />
                  Trend Suggestions
                </span>
                <div className="flex items-center gap-3">
                  {showTabs && (
                    <div
                      role="tablist"
                      aria-label="Suggestions from"
                      onKeyDown={onTabKeyDown}
                      className="inline-flex rounded-lg border border-line-input bg-page p-0.5"
                    >
                      <SegmentTab
                        id={tabId('team')}
                        panelId={panelId}
                        selected={view === 'team'}
                        onSelect={() => setChosenView('team')}
                      >
                        Used by teammates{' '}
                        <span className="ml-1 rounded-full bg-tint px-1.5 text-[0.625rem] font-semibold tabular-nums text-tint-fg">
                          {teamSuggestions.length}
                        </span>
                      </SegmentTab>
                      <SegmentTab
                        id={tabId('ai')}
                        panelId={panelId}
                        selected={view === 'ai'}
                        onSelect={() => setChosenView('ai')}
                      >
                        AI suggestions
                      </SegmentTab>
                    </div>
                  )}
                  {!teamLoading && view === 'ai' && (
                    <button
                      type="button"
                      onClick={() => void suggestionsQuery.refetch()}
                      disabled={suggestionsQuery.isFetching}
                      className="focus-ring rounded text-xs font-medium text-link hover:text-action-hover disabled:opacity-50"
                    >
                      {suggestionsQuery.isFetching ? 'Refreshing…' : 'Refresh'}
                    </button>
                  )}
                </div>
              </div>

              <div
                {...(showTabs
                  ? { role: 'tabpanel', id: panelId, 'aria-labelledby': tabId(view) }
                  : {})}
              >
                {teamLoading ? (
                  <p className="text-xs text-ink-faint">Loading suggestions…</p>
                ) : view === 'team' ? (
                  <>
                    <p className="mb-2 text-xs text-ink-muted">
                      Topics your teammates track that aren&rsquo;t in your list yet, most-used
                      first.
                    </p>
                    <ul className="flex flex-wrap gap-2">
                      {teamSuggestions.map((topic) => {
                        const key = topicKey(topic.label);
                        return (
                          <li key={key}>
                            <TeammateTopicChip
                              topic={topic}
                              adding={adopt.isPending && adopt.variables?.label === topic.label}
                              open={openUsedBy === key}
                              addButtonId={chipAddId(key)}
                              onAdd={() => adopt.mutate(topic)}
                              onToggle={() => setOpenUsedBy((o) => (o === key ? null : key))}
                              onDismiss={closeUsedBy}
                            />
                          </li>
                        );
                      })}
                    </ul>
                    {adopt.isError && (
                      <p role="alert" className="mt-2 text-xs text-red-600 dark:text-error-dark">
                        {adopt.error instanceof ApiRequestError
                          ? adopt.error.message
                          : 'Could not add the topic.'}
                      </p>
                    )}
                  </>
                ) : suggestionsQuery.isLoading ? (
                  <p className="text-xs text-ink-faint">
                    Generating suggestions from your Brand, Company &amp; Personal Voice…
                  </p>
                ) : suggestions.length === 0 ? (
                  <p className="text-xs text-ink-faint">
                    No new suggestions right now. Set your Brand/Company/Personal Voice for sharper
                    ideas, or check back after adding a few topics.
                  </p>
                ) : (
                  <ul className="flex flex-wrap gap-2">
                    {suggestions.map((phrase) => {
                      const pending = create.isPending && create.variables === phrase;
                      return (
                        <li key={phrase}>
                          <button
                            type="button"
                            disabled={pending}
                            onClick={() => create.mutate(phrase)}
                            className="focus-ring inline-flex items-center gap-1 rounded-full border border-line-input bg-card px-2.5 py-1 text-xs font-medium text-link transition-colors hover:bg-tint disabled:opacity-50"
                          >
                            <Plus className="h-3 w-3" aria-hidden="true" />
                            {phrase}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}

                {!teamLoading && view === 'ai' && create.isError && (
                  <p className="mt-2 text-xs text-red-600 dark:text-error-dark">Could not add the suggestion.</p>
                )}
              </div>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

/** One half of the "Used by teammates | AI suggestions" switch. */
function SegmentTab({
  id,
  panelId,
  selected,
  onSelect,
  children,
}: {
  id: string;
  panelId: string;
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-selected={selected}
      aria-controls={panelId}
      // Roving tabindex: Tab reaches the selected tab only; the arrows move between the two.
      tabIndex={selected ? 0 : -1}
      onClick={onSelect}
      className={cn(
        'focus-ring inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
        selected ? 'bg-card text-ink shadow-sm' : 'text-ink-muted hover:text-ink',
        selected && hcFillSelected,
      )}
    >
      {children}
    </button>
  );
}
